import {
    SessionResponse,
    TokenExchangeResponse,
    AvailableTokenResponse,
    TransactionResponse,
    TransactionStatusResponse,
    TransferResponse,
    ConsumeResponse,
    ConfirmConsumeTokenResponse,
    UpdateConsumeStatusResponse,
  } from '../lib/types';
  import { store, clearSession } from './store';

  const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3008';
  const API_KEY = process.env.NEXT_PUBLIC_API_KEY || 'pk_8d2a0da71ee2c91e7a8de658f8b00c73f2032dfd97b0c7f9bd3b06a7cf2c326c';

  interface ApiRequestOptions extends RequestInit {
    skipAuth?: boolean;
    // Send auth token but don't clear session/redirect on 401 (for non-critical endpoints)
    noAutoLogout?: boolean;
  }

  async function apiRequest<T>(endpoint: string, options: ApiRequestOptions = {}): Promise<T> {
    const { skipAuth, noAutoLogout, ...fetchOptions } = options;

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'x-api-key': API_KEY,
      ...(fetchOptions.headers as HeadersInit),
    };

    const sessionToken = store.getState().auth.sessionToken;

    if (sessionToken && !skipAuth) {
      headers['Authorization'] = `Bearer ${sessionToken}`;
    }

    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...fetchOptions,
        headers,
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle token expiry - clear session and notify app
        // Skip this for noAutoLogout calls (price queries, non-critical endpoints)
        if (response.status === 401 && !skipAuth && !noAutoLogout) {
          store.dispatch(clearSession());
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('auth:token-expired'));
          }
        }
        throw new Error(data.error || data.message || 'Request failed');
      }

      return data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }
  
  export const api = {
    // Authentication
    createSession: (username: string, password: string) =>
      apiRequest<SessionResponse>('/create-session', {
        method: 'POST',
        body: JSON.stringify({ username, password }),
        skipAuth: true,
      }),
  
    // Wallet
    linkWallet: (walletAddress: string) =>
      apiRequest<{ success: boolean; message: string }>('/api/link-wallet', {
        method: 'POST',
        body: JSON.stringify({ walletAddress }),
      }),
  
    getPlatformRedirectUrl: () =>
      apiRequest<{ success: boolean; redirectUrl: string | null }>('/api/platform-redirect-url'),
  
    // Functions API
    getTokenExchangeValue: (usd: number = 1) =>
      apiRequest<TokenExchangeResponse>('/api/functions', {
        method: 'POST',
        body: JSON.stringify({
          type: 'IRMA_TOKEN_EXCHANGE_VALUE',
          usd,
        }),
        noAutoLogout: true,
      }),
  
    getAvailableTokens: (userId: string) =>
      apiRequest<AvailableTokenResponse>('/api/functions', {
        method: 'POST',
        body: JSON.stringify({
          type: 'AVAILABLE_TOKEN',
          userId,
        }),
      }),
  
    getTransactions: (userId: string, transactionType: string = 'ALL') =>
      apiRequest<TransactionResponse>('/api/functions', {
        method: 'POST',
        body: JSON.stringify({
          type: 'GET_TRANSACTION',
          userId,
          transactionType,
        }),
      }),
  
    getTransactionStatus: (userId: string, transactionId: string) =>
      apiRequest<TransactionStatusResponse>('/api/functions', {
        method: 'POST',
        body: JSON.stringify({
          type: 'GET_TRANSACTION_STATUS',
          userId,
          transactionId,
        }),
      }),
  
    transferUsdToIrma: (userId: string, usd: number) =>
      apiRequest<TransferResponse>('/api/functions', {
        method: 'POST',
        body: JSON.stringify({
          type: 'TRANSFER_USD_TO_IRMA_TOKEN',
          userId,
          usd,
        }),
      }),
  
    consumeIrmaToken: (userId: string, usd: number) =>
      apiRequest<ConsumeResponse>('/api/functions', {
        method: 'POST',
        body: JSON.stringify({
          type: 'CONSUME_IRMA_TOKEN',
          userId,
          usd,
        }),
      }),

    confirmConsumeToken: (transactionId: string) =>
      apiRequest<ConfirmConsumeTokenResponse>('/api/functions', {
        method: 'POST',
        body: JSON.stringify({
          type: 'CONFIRM_CONSUME_TOKEN',
          transactionId,
        }),
      }),

    getSubscriptionStatus: (transactionId: string) =>
      apiRequest<ConfirmConsumeTokenResponse>(`/api/subscription-status?transactionId=${encodeURIComponent(transactionId)}`),

    updateConsumeStatus: (transactionId: string, consumeStatus: 'completed' | 'failed', txSignature?: string) =>
      apiRequest<UpdateConsumeStatusResponse>('/api/functions', {
        method: 'POST',
        body: JSON.stringify({
          type: 'UPDATE_CONSUME_STATUS',
          transactionId,
          consumeStatus,
          ...(txSignature ? { txSignature } : {}),
        }),
      }),

    // Wallet Connect Flow (public endpoints)
    checkWalletStatus: (platformCode: string, email: string, existingToken?: string | null) =>
      apiRequest<{
        success: boolean;
        account_exists: boolean;
        wallet_connected: boolean;
        wallet_address: string | null;
        token_valid: boolean;
        sessionToken: string | null;
        tokenExpiry?: string | null;
      }>('/api/wallet-connect/check-status', {
        method: 'POST',
        body: JSON.stringify({ platform_code: platformCode, email, sessionToken: existingToken || undefined }),
        skipAuth: true,
      }),

    connectWalletForPlatform: (platformCode: string, email: string, walletAddress: string, force: boolean = false) =>
      apiRequest<{
        success: boolean;
        message: string;
        wallet_address?: string;
        is_new_user?: boolean;
        wallet_mismatch?: boolean;
        existing_wallet?: string;
        new_wallet?: string;
        wallet_replaced?: boolean;
        sessionToken?: string;
        tokenExpiry?: string;
      }>('/api/wallet-connect/connect', {
        method: 'POST',
        body: JSON.stringify({ platform_code: platformCode, email, wallet_address: walletAddress, force }),
        skipAuth: true,
      }),

    syncWalletToPlatform: (platformCode: string, email: string, walletAddress: string) =>
      apiRequest<{
        success: boolean;
        synced: boolean;
        message: string;
      }>('/api/wallet-connect/sync-to-platform', {
        method: 'POST',
        body: JSON.stringify({ platform_code: platformCode, email, wallet_address: walletAddress }),
        skipAuth: true,
      }),

    getBackToPlatformUrl: (platformCode: string) =>
      apiRequest<{
        success: boolean;
        redirect_url: string | null;
        platform_name: string;
        platform_code: string;
      }>(`/api/wallet-connect/redirect-url/${encodeURIComponent(platformCode)}`, {
        skipAuth: true,
      }),
  };